"use client";

import { useCallback, useEffect, useState } from "react";
import { buttonClass } from "./buttons";
import { Banner } from "./ui";
import { useFormAction, type FormAction } from "./useFormAction";

/**
 * The three writes this file allows. Injected, so that this file holds no
 * transport at all: it renders the outcome and never learns how the write
 * travelled (R22 T14).
 *
 * **Who may edit and who may delete are not the same question** (H42), and the
 * server answers both: editing somebody else's words can never be permitted,
 * while deleting can, because asking a Manager to remove a note is a real and
 * reasonable thing to do. The two arrive per note as `canEdit` and `canDelete`,
 * already decided.
 */
export interface NoteCommands {
  add: FormAction;
  edit: FormAction;
  remove: FormAction;
}

/**
 * The Consumer File's notes (H42, docs/ux/screens/CustomerFileScreen.md).
 *
 * **Nothing here is ever rendered to a Consumer**, and the reason is one level
 * below this component: no consumer-facing query reads the `customer_records`
 * schema, so there is no path by which this data reaches a page a Consumer can
 * open. This file only has to be honest about who may *write*.
 *
 * The two permissions are computed on the server and arrive as booleans
 * (`canEdit`, `canDelete`), rather than being re-derived here from the tier. A
 * client that decides who may edit is a client that can be told otherwise; the
 * commands check again anyway, and these flags only decide whether a control is
 * worth rendering.
 *
 * **`maxLength` is a prop rather than an import**, for a mechanical reason
 * worth remembering: `customer_records/api.ts` is the data path, and a client
 * component that imports one constant from it drags the whole server module
 * (and its database client) into the browser bundle, which fails the build. The
 * page owns the import; this file owns the textarea.
 */

export interface NoteView {
  id: string;
  body: string;
  authorName: string;
  createdAt: string;
  updatedAt: string;
  /** "12 Aug 2026 · Initial Assessment", or null for a general note. */
  visit: string | null;
  canEdit: boolean;
  canDelete: boolean;
}

export interface VisitOption {
  id: string;
  label: string;
}

export function CustomerNotes({
  consumerId,
  notes,
  visits,
  maxLength,
  commands,
}: {
  consumerId: string;
  notes: NoteView[];
  visits: VisitOption[];
  maxLength: number;
  commands: NoteCommands;
}) {
  const {
    state: addState,
    pending: adding,
    onSubmit: addSubmit,
  } = useFormAction(commands.add);
  const [editingId, setEditingId] = useState<string | null>(null);
  // Stable, so the editor's "close me when saved" effect does not re-run on
  // every render of the list.
  const stopEditing = useCallback(() => setEditingId(null), []);

  return (
    <>
      <section className="mb-6 rounded-xl border border-line bg-surface p-5 shadow-sm">
        <h2 className="mb-1 font-bold text-ink">Add a note</h2>
        <p className="mb-4 text-sm text-muted">
          Everyone at this clinic can read this. The customer cannot.
        </p>

        {addState.formError && <Banner tone="danger">{addState.formError}</Banner>}

        <form onSubmit={addSubmit} className="space-y-3">
          <input type="hidden" name="consumerId" value={consumerId} />
          <label className="block text-sm">
            <span className="sr-only">Note</span>
            <textarea
              name="body"
              rows={4}
              maxLength={maxLength}
              defaultValue={addState.values?.body}
              placeholder="What is worth remembering about this customer?"
              className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-ink"
            />
            {addState.fieldErrors?.body && (
              <span className="mt-1 block text-danger">{addState.fieldErrors.body}</span>
            )}
          </label>

          {visits.length > 0 && (
            <label className="block text-sm">
              <span className="font-semibold text-ink">About which visit?</span>
              <select
                name="bookingId"
                defaultValue=""
                className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-ink"
              >
                <option value="">General note</option>
                {visits.map((visit) => (
                  <option key={visit.id} value={visit.id}>
                    {visit.label}
                  </option>
                ))}
              </select>
            </label>
          )}

          <button type="submit" disabled={adding} className={buttonClass("primary")}>
            {adding ? "Saving…" : "Save note"}
          </button>
        </form>
      </section>

      <section>
        <h2 className="mb-3 font-bold text-ink">Notes</h2>
        {notes.length === 0 ? (
          <p className="rounded-xl border border-line bg-surface p-5 text-sm text-body shadow-sm">
            Nothing written about this customer yet.
          </p>
        ) : (
          <ul className="space-y-3">
            {notes.map((note) => (
              <li
                key={note.id}
                className="rounded-xl border border-line bg-surface p-5 shadow-sm"
              >
                {note.visit && (
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-accent">
                    {note.visit}
                  </p>
                )}

                {editingId === note.id ? (
                  <EditForm
                    note={note}
                    consumerId={consumerId}
                    maxLength={maxLength}
                    onDone={stopEditing}
                    edit={commands.edit}
                  />
                ) : (
                  <p className="whitespace-pre-wrap text-sm text-ink">{note.body}</p>
                )}

                <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted">
                  <span>
                    {note.authorName} · {note.createdAt}
                    {note.updatedAt !== note.createdAt && ` · edited ${note.updatedAt}`}
                  </span>
                  {note.canEdit && editingId !== note.id && (
                    <button
                      type="button"
                      onClick={() => setEditingId(note.id)}
                      className="font-semibold text-accent hover:underline"
                    >
                      Edit
                    </button>
                  )}
                  {note.canDelete && (
                    <DeleteButton
                      noteId={note.id}
                      consumerId={consumerId}
                      remove={commands.remove}
                    />
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

function EditForm({
  note,
  consumerId,
  maxLength,
  onDone,
  edit,
}: {
  note: NoteView;
  consumerId: string;
  maxLength: number;
  onDone: () => void;
  edit: FormAction;
}) {
  const { state, pending, onSubmit: action } = useFormAction(edit);
  // Closing the editor is a parent state change, so it belongs in an effect
  // rather than in the render pass: calling `onDone()` inline updates another
  // component while this one renders, which React warns about and which turns
  // into a real double-render once the list gets long.
  useEffect(() => {
    if (state.success) onDone();
  }, [state.success, onDone]);

  return (
    <form onSubmit={action} className="space-y-2">
      <input type="hidden" name="noteId" value={note.id} />
      <input type="hidden" name="consumerId" value={consumerId} />
      <textarea
        name="body"
        rows={4}
        maxLength={maxLength}
        defaultValue={note.body}
        className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink"
      />
      {state.fieldErrors?.body && (
        <p className="text-sm text-danger">{state.fieldErrors.body}</p>
      )}
      {state.formError && <Banner tone="danger">{state.formError}</Banner>}
      <div className="flex items-center gap-2">
        <button type="submit" disabled={pending} className={buttonClass("primary")}>
          {pending ? "Saving…" : "Save"}
        </button>
        <button type="button" onClick={onDone} className={buttonClass("secondary")}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/**
 * Deletion asks first. It is the only irreversible control on the screen (a
 * note is deleted outright, not hidden), and the thing being destroyed is
 * somebody's written observation about a patient.
 */
function DeleteButton({
  noteId,
  consumerId,
  remove,
}: {
  noteId: string;
  consumerId: string;
  remove: FormAction;
}) {
  const { state, pending, onSubmit: action } = useFormAction(remove);
  const [confirming, setConfirming] = useState(false);

  if (!confirming) {
    return (
      <button
        type="button"
        onClick={() => setConfirming(true)}
        className="font-semibold text-danger hover:underline"
      >
        Delete
      </button>
    );
  }

  return (
    <form onSubmit={action} className="inline-flex items-center gap-2">
      <input type="hidden" name="noteId" value={noteId} />
      <input type="hidden" name="consumerId" value={consumerId} />
      <span>Delete this note?</span>
      <button
        type="submit"
        disabled={pending}
        className="font-semibold text-danger hover:underline"
      >
        {pending ? "Deleting…" : "Yes, delete"}
      </button>
      <button
        type="button"
        onClick={() => setConfirming(false)}
        className="font-semibold text-accent hover:underline"
      >
        Keep it
      </button>
      {state.formError && <span className="text-danger">{state.formError}</span>}
    </form>
  );
}
